export const SESSION_WARNING_EVENT = 'jidoka:session-warning';
/** Inactivity timeout before forced sign-out (7 days). */
export const SESSION_TIMEOUT_MS = 7 * 24 * 60 * 60 * 1000;
export const SESSION_WARNING_MS = 5 * 60 * 1000;
export const ACTIVITY_WRITE_THROTTLE_MS = 30 * 1000;

const CHANNEL_NAME = 'jidoka-auth-session';
const KEY_PREFIX = 'JIDOKA_LAST_ACTIVITY:';

export interface SessionWarningDetail {
  remainingMs: number;
}

export class SessionManager {
  private readonly key: string;
  private channel: BroadcastChannel | null = null;
  private timer: number | null = null;
  private lastRecordedAt = 0;
  private warningShown = false;
  private stopped = false;

  constructor(
    private readonly uid: string,
    private readonly onTimeout: () => void | Promise<void>,
    private readonly signedInAt = 0
  ) {
    this.key = `${KEY_PREFIX}${uid}`;
  }

  start(): boolean {
    let stored = Number(localStorage.getItem(this.key));
    const now = Date.now();
    // A sign-in newer than the stored activity means the user just
    // authenticated; an old timestamp from a previous session must not
    // immediately sign them back out.
    if (Number.isFinite(stored) && stored > 0 && this.signedInAt > stored) {
      this.recordActivity(now, true);
      stored = now;
    }
    if (Number.isFinite(stored) && stored > 0 && now - stored >= SESSION_TIMEOUT_MS) {
      void this.timeout();
      return false;
    }

    if (!stored) this.recordActivity(now, true);
    this.channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
    this.channel?.addEventListener('message', this.handleChannelMessage);
    window.addEventListener('storage', this.handleStorage);
    window.addEventListener('focus', this.evaluate);
    window.addEventListener('pageshow', this.evaluate);
    document.addEventListener('visibilitychange', this.evaluate);
    ['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach(event =>
      window.addEventListener(event, this.handleActivity, { passive: true })
    );
    this.schedule();
    return true;
  }

  stop(clear = false): void {
    this.stopped = true;
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.channel?.removeEventListener('message', this.handleChannelMessage);
    this.channel?.close();
    window.removeEventListener('storage', this.handleStorage);
    window.removeEventListener('focus', this.evaluate);
    window.removeEventListener('pageshow', this.evaluate);
    document.removeEventListener('visibilitychange', this.evaluate);
    ['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach(event =>
      window.removeEventListener(event, this.handleActivity)
    );
    this.removeWarning();
    if (clear) localStorage.removeItem(this.key);
  }

  staySignedIn = (): void => this.recordActivity(Date.now(), true);

  private handleActivity = (): void => this.recordActivity(Date.now(), false);

  private recordActivity(at: number, force: boolean): void {
    if (this.stopped || (!force && at - this.lastRecordedAt < ACTIVITY_WRITE_THROTTLE_MS)) return;
    this.lastRecordedAt = at;
    localStorage.setItem(this.key, String(at));
    this.channel?.postMessage({ uid: this.uid, at });
    this.warningShown = false;
    this.removeWarning();
    this.schedule();
  }

  private handleStorage = (event: StorageEvent): void => {
    if (event.key === this.key) this.schedule();
  }

  private handleChannelMessage = (event: MessageEvent<{ uid?: string }>): void => {
    if (event.data?.uid === this.uid) this.schedule();
  }

  private evaluate = (): void => this.schedule();

  private schedule(): void {
    if (this.stopped) return;
    if (this.timer !== null) window.clearTimeout(this.timer);
    const lastActivity = Number(localStorage.getItem(this.key)) || Date.now();
    const remaining = SESSION_TIMEOUT_MS - (Date.now() - lastActivity);
    if (remaining <= 0) {
      void this.timeout();
      return;
    }
    if (remaining <= SESSION_WARNING_MS) {
      this.showWarning(remaining);
      this.timer = window.setTimeout(() => this.schedule(), Math.min(1000, remaining));
    } else {
      this.timer = window.setTimeout(() => this.schedule(), remaining - SESSION_WARNING_MS);
    }
  }

  private showWarning(remainingMs: number): void {
    window.dispatchEvent(new CustomEvent<SessionWarningDetail>(SESSION_WARNING_EVENT, { detail: { remainingMs } }));
    let modal = document.getElementById('session-timeout-dialog') as HTMLDialogElement | null;
    if (!modal) {
      modal = document.createElement('dialog');
      modal.id = 'session-timeout-dialog';
      modal.className = 'session-timeout-dialog';
      modal.setAttribute('aria-labelledby', 'session-timeout-title');
      const title = document.createElement('h2');
      title.id = 'session-timeout-title';
      title.textContent = 'Your session is about to expire';
      const message = document.createElement('p');
      message.className = 'session-timeout-message';
      const actions = document.createElement('div');
      actions.className = 'session-timeout-actions';
      const signOutButton = document.createElement('button');
      signOutButton.type = 'button';
      signOutButton.textContent = 'Sign out now';
      signOutButton.addEventListener('click', () => void this.timeout());
      const stayButton = document.createElement('button');
      stayButton.type = 'button';
      stayButton.className = 'primary';
      stayButton.textContent = 'Stay signed in';
      stayButton.addEventListener('click', this.staySignedIn);
      actions.append(signOutButton, stayButton);
      modal.append(title, message, actions);
      document.body.appendChild(modal);
    }
    const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    const message = modal.querySelector('.session-timeout-message');
    if (message) message.textContent = `You will be signed out in ${minutes}:${remainder.toString().padStart(2, '0')} due to inactivity.`;
    if (!this.warningShown && !modal.open) modal.showModal();
    this.warningShown = true;
  }

  private removeWarning(): void {
    const modal = document.getElementById('session-timeout-dialog') as HTMLDialogElement | null;
    if (modal?.open) modal.close();
    modal?.remove();
  }

  private async timeout(): Promise<void> {
    if (this.stopped) return;
    this.stop(true);
    await this.onTimeout();
  }
}
